import { useEffect, useState } from 'react';
import { IBaseResponse } from '@nkr/models/base-response';
import { IBusinessCardsRequest } from '@nkr/models/microsites/business-cards-request';
import { AuthActionCreators } from '@nkr/store/authorization';
import { useAppHookContext } from '@nkr/utils/rest-api';

interface IRetVal {
    request: IBusinessCardsRequest,
    loading: boolean,
    error: string,
    update: (request: IBusinessCardsRequest) => void
}

const useBusinessCardsRequest = (micrositeRecipientId: number): IRetVal => {
    const { dispatch } = useAppHookContext();
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [request, setRequest] = useState<IBusinessCardsRequest>(null);

    useEffect(() => {
        if (micrositeRecipientId) {
            setLoading(true);
            setError('');

            const fetchData = async () => {
                let config = {
                    url: `${__API__}/microsite/${micrositeRecipientId}/business-cards-request`,
                };

                try {
                    const response: IBaseResponse = await dispatch(AuthActionCreators.apiGet(config));

                    if (response && response.data.results && response.data.success) {
                        setRequest(response.data.results);
                    } else {
                        setError('Unable to load data');
                        setRequest(null);
                    }

                } catch (err) {
                    setRequest(null);
                    setError(err);
                } finally {
                    setLoading(false);
                }
            }

            fetchData();
        }
    }, [micrositeRecipientId]);

    const update = (request: IBusinessCardsRequest) => {
        setLoading(true);
        setError('');

        const fetchData = async () => {
            let config = {
                url: `${__API__}/microsite/${micrositeRecipientId}/business-cards-request`,
                data: request
            };

            try {
                const response: IBaseResponse = await dispatch(AuthActionCreators.apiPost(config));

                if (response && response.data.results && response.data.success) {
                    setRequest(response.data.results);
                } else {
                    setError('Unable to save data');
                    setRequest(null);
                }

            } catch (err) {
                setRequest(null);
                setError(err);
            } finally {
                setLoading(false);
            }
        }

        fetchData();
    }

    return { request, loading, error, update };
}

export default useBusinessCardsRequest;
